import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import * as statsigActions from '../../actions/statsig-actions';
import { checkIfStatsigUpdateIsNeeded } from '../../utils/events';
import { useStatsig } from '../hooks/useStatsig';
import ErrorPage from '../ErrorPage';

const withStatsigEventUpdate = (WrappedComponent) => (props) => {
  const { match } = props;
  const eventId = parseInt(match?.params?.eventId);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { updateStatsigUser, initializeStatsigUser, statsigUser } = useStatsig();
  const { eventDataForStatsig, redirectUrl } = useSelector(({ statsig }) => statsig);
  const { error } = eventDataForStatsig;

  useEffect(() => {
    if (user) {
      let customFields = null;
      if (eventDataForStatsig.data) {
        customFields = {
          sportTypeId: parseInt(eventDataForStatsig.data.sportTypeId),
          clubId: parseInt(eventDataForStatsig.data.clubId),
          eventId,
        };
      }
      initializeStatsigUser(customFields);
    }
  }, [user]);

  const isReady = useMemo(() => {
    const isUserLoggedIn = user?.id != null;

    const isCurrentEventStatsigDataLoaded = eventDataForStatsig.data != null && parseInt(eventDataForStatsig.data.eventId) === parseInt(eventId);

    if (!isUserLoggedIn) {
      return isCurrentEventStatsigDataLoaded;
    }

    const isStatSigUserSameAsLoggedInUser = statsigUser?.userID != null && parseInt(statsigUser.userID) === parseInt(user.id);

    const isStatsigUserUpdateNeeded = checkIfStatsigUpdateIsNeeded(
      eventDataForStatsig.data,
      statsigUser,
    );

    return (
      isStatSigUserSameAsLoggedInUser &&
      isCurrentEventStatsigDataLoaded &&
      !isStatsigUserUpdateNeeded
    );
  }, [eventId, eventDataForStatsig.data, statsigUser, user?.id]);

  // Step 1: Fetch event data for statsig if needed
  useEffect(() => {
    const shouldFetchStatsigEventData = !eventDataForStatsig.loading &&
      !eventDataForStatsig.error &&
      (eventDataForStatsig.data == null || parseInt(eventDataForStatsig.data?.eventId) !== parseInt(eventId));

    if (shouldFetchStatsigEventData) {
      dispatch(statsigActions.getEventForUpdatingStatsigUser(parseInt(eventId)));
    }
  }, [eventId, eventDataForStatsig.loading, eventDataForStatsig.error, eventDataForStatsig.data]);

  // Step 2: Update statsig user once data is available
  useEffect(() => {
    if (!eventDataForStatsig?.data) {
      return;
    }

    const loadedEventId = parseInt(eventDataForStatsig.data?.eventId);
    if (loadedEventId !== eventId) {
      return; // avoid acting on stale data
    }

    if (checkIfStatsigUpdateIsNeeded(eventDataForStatsig.data, statsigUser)) {
      updateStatsigUser({
        sportTypeId: parseInt(eventDataForStatsig.data.sportTypeId),
        clubId: parseInt(eventDataForStatsig.data.clubId),
        eventId,
      });
    }
  }, [eventDataForStatsig?.data, statsigUser, eventId, updateStatsigUser]);

  if (error && redirectUrl) {
    const handleBack = () => {
      dispatch(statsigActions.clearStatsigError());
      window.location.replace(redirectUrl);
    };

    return (
        <ErrorPage
            subtitle="This event could not be found."
            showReloadButton={false}
            showBackButton={true}
            onBack={handleBack}
            data-testid="statsig-event.error-page"
        />
    );
  }

  // Wait until statsig is updated - loader is displayed from the reducer
  if (!isReady) {
    return null;
  }

  return <WrappedComponent {...props} />;
};

export default withStatsigEventUpdate;
